import { defineStore } from 'pinia';
import { scrapeWebsite } from '@/api/scraper';

interface Avatar {
  Stats: {
    Value: number;
  };
  Fluid: string;
  Name: string;
}

interface AvatarStats {
  Value: number | undefined;
  Description?: string;
  Name?: string;
}

interface Chat {
  MAID: object;
  Messages: Array<{ Content: string; Type: string }>;
}

interface Slide {
  Avatar: Avatar;
  Slide1: { Chat: Chat };
  Slide2: { Chat: Chat };
  SuggestedSlide: number;
  WhySuggest: string;
}

interface Status {
  Percent: number;
  Text: string;
}

interface State {
  avatarStats: AvatarStats[];
  Status: Status | null;
  allSliders: Slide[];
  requestFailed: boolean;
  deepAnalysisId: string | null;
  // NEW: store the new Tenant ID
  newtenantid?: string | null;
}

/* -----------------------
    LocalStorage Helpers
------------------------ */
function getAvatarStatsFromLocalStorage(index: number): AvatarStats {
  return {
    Value: Number(localStorage.getItem(`avatarStatsValue${index}`) || '0'),
    Description:
      localStorage.getItem(`avatarStatsDescription${index}`) ?? undefined,
    Name: localStorage.getItem(`avatarStatsName${index}`) ?? undefined
  };
}

function getSlidesFromLocalStorage(): Slide[] {
  const savedSlides = localStorage.getItem('allSliders');
  return savedSlides ? JSON.parse(savedSlides) : [];
}

function setAvatarStatsInLocalStorage(index: number, stats: AvatarStats): void {
  localStorage.setItem(`avatarStatsValue${index}`, String(stats.Value));
  if (stats.Description) {
    localStorage.setItem(`avatarStatsDescription${index}`, stats.Description);
  }
  if (stats.Name) {
    localStorage.setItem(`avatarStatsName${index}`, stats.Name);
  }
}

function setSlidesInLocalStorage(slides: Slide[]): void {
  localStorage.setItem('allSliders', JSON.stringify(slides));
}

function getDeepAnalysisIdFromLocalStorage(): string | null {
  return localStorage.getItem('deepAnalysisId');
}

function setDeepAnalysisIdInLocalStorage(id: string): void {
  localStorage.setItem('deepAnalysisId', id);
}

// NEW: store and retrieve new Tenant ID
function getNewTenantIdFromLocalStorage(): string | null {
  return localStorage.getItem('newtenantid');
}
function setNewTenantIdInLocalStorage(id: string): void {
  localStorage.setItem('newtenantid', id);
}

/* -----------------------
         Store
------------------------ */
export const useScraperStore = defineStore({
  id: 'scraperStore',
  state: (): State => ({
    avatarStats: [0, 1, 2].map(getAvatarStatsFromLocalStorage),
    Status: null,
    allSliders: getSlidesFromLocalStorage(),
    requestFailed: false,

    deepAnalysisId: getDeepAnalysisIdFromLocalStorage(),

    // Initialize newtenantid from localStorage as well
    newtenantid: getNewTenantIdFromLocalStorage()
  }),
  actions: {
    async fetchData(websiteUrl: string) {
      try {
        const deepAnalysisResponse = await scrapeWebsite(websiteUrl);

        deepAnalysisResponse.Avatars.forEach(
          (avatar: Avatar, index: number) => {
            const stats: AvatarStats = {
              Value: avatar.Stats.Value,
              Description: avatar.Fluid,
              Name: avatar.Name
            };
            this.avatarStats[index] = stats;
            setAvatarStatsInLocalStorage(index, stats);
          }
        );

        this.Status = deepAnalysisResponse.Status;
        this.allSliders = deepAnalysisResponse.AllSliders;
        setSlidesInLocalStorage(this.allSliders);

        this.requestFailed = false;
      } catch (error) {
        console.error(error);
        this.requestFailed = true;
      }
    },
    setDeepAnalysisId(id: string) {
      this.deepAnalysisId = id;
      setDeepAnalysisIdInLocalStorage(id);
    },

    // NEW ACTION: set the new Tenant ID and store in localStorage
    setNewTenantId(id: string) {
      this.newtenantid = id;
      setNewTenantIdInLocalStorage(id);
    }
  }
});
